import {
  ConsultationFragment,
  Document,
  TranscriptChunk,
} from '@/shared/api/transcriptor/transcriptorApi';

import {
  ConsultationRecording,
  NANOSECONDS_PER_SECOND,
  OBJECT_ID_LENGTH,
  PROCESSING_PROGRESS_MAP,
} from '../config';

/**
 * Extract transcript chunks with timestamps from consultation fragments
 */
export const getFragmentDurationSec = (
  fragment: ConsultationFragment,
): number => {
  if (fragment.audio?.duration != null) {
    return fragment.audio.duration / NANOSECONDS_PER_SECOND;
  }
  return (fragment.transcript?.chunks ?? []).reduce(
    (max, chunk) => Math.max(max, chunk.end),
    0,
  );
};

/**
 * Fragments arrive in backend storage order; recordings are shown oldest first.
 * A fragment without a parsable `created_at` keeps its original position at the
 * end instead of jumping to the front on `Date.parse` returning NaN.
 */
const sortFragmentsByCreatedAt = (
  fragments: ConsultationFragment[],
): ConsultationFragment[] =>
  fragments
    .map((fragment, index) => {
      const createdAt = Date.parse(fragment.created_at);

      return {
        fragment,
        index,
        createdAt: Number.isNaN(createdAt)
          ? Number.MAX_SAFE_INTEGER
          : createdAt,
      };
    })
    .sort(
      (left, right) =>
        left.createdAt - right.createdAt || left.index - right.index,
    )
    .map((item) => item.fragment);

export const extractRecordingsFromFragments = (
  fragments?: ConsultationFragment[],
): ConsultationRecording[] => {
  if (!fragments || fragments.length === 0) {
    return [];
  }

  return sortFragmentsByCreatedAt(fragments).map((fragment) => ({
    id: fragment.id,
    url: fragment.audio?.url ?? null,
    createdAt: new Date(fragment.created_at),
    durationSec: getFragmentDurationSec(fragment),
    chunks: fragment.transcript?.chunks ?? [],
    stopReason: fragment.stop_reason ?? null,
  }));
};

export const extractTranscriptChunksFromFragments = (
  fragments?: ConsultationFragment[],
): TranscriptChunk[] => {
  if (!fragments || fragments.length === 0) {
    return [];
  }

  const merged = fragments.reduce<{
    chunks: TranscriptChunk[];
    offset: number;
  }>(
    (acc, fragment) => {
      const shiftedChunks = (fragment.transcript?.chunks ?? []).map(
        (chunk) => ({
          ...chunk,
          start: chunk.start + acc.offset,
          end: chunk.end + acc.offset,
        }),
      );

      return {
        chunks: [...acc.chunks, ...shiftedChunks],
        offset: acc.offset + getFragmentDurationSec(fragment),
      };
    },
    { chunks: [], offset: 0 },
  );

  return merged.chunks;
};

/**
 * Find the first encounter document whose code starts with the given prefix.
 * Encounter document codes carry language/version suffixes — backend matches
 * the same way (see transcriptor `DOCUMENT_CODE_PREFIXES`).
 */
export const findDocumentByCodePrefix = (
  documents: Document[] | undefined,
  prefix: string,
): Document | undefined =>
  documents?.find((doc) => doc.code?.startsWith(prefix));

/**
 * Stable identifier for a document used as a tab value / route segment.
 * Prefers `template_series_id` (stable across versions), then `code`, then `id`.
 */
export const getDocumentTabValue = (doc: Document): string =>
  doc.template_series_id ?? doc.code ?? doc.id ?? '';

/**
 * Human-readable label for a document tab. Prefers `tab_title`, then `name`,
 * then `code`.
 */
export const getDocumentTabLabel = (doc: Document): string =>
  doc.tab_title?.trim() || doc.name?.trim() || doc.code || '';

/**
 * Map API status to local status type
 */
export const mapApiStatus = (
  apiStatus: string,
): 'draft' | 'completed' | 'processing' => {
  // Direct mapping from API statuses to local statuses
  const statusMap: Record<string, 'draft' | 'completed' | 'processing'> = {
    created: 'draft',
    recording: 'processing',
    processing: 'processing',
    succeeded: 'completed',
    failed: 'draft',
  };

  return statusMap[apiStatus] || 'processing';
};

/**
 * Calculate processing progress based on status
 */
export const getProcessingProgress = (status: string): number => {
  return (
    PROCESSING_PROGRESS_MAP[status as keyof typeof PROCESSING_PROGRESS_MAP] ||
    PROCESSING_PROGRESS_MAP.default
  );
};

/**
 * Generate ObjectId-like string (24 hex characters)
 */
export const generateObjectId = (): string => {
  const timestamp = Math.floor(Date.now() / 1000)
    .toString(16)
    .padStart(8, '0');
  const random = Array.from({ length: OBJECT_ID_LENGTH - 8 }, () =>
    Math.floor(Math.random() * 16).toString(16),
  ).join('');
  return timestamp + random;
};

/**
 * Convert Blob to base64 string
 */
export const blobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== 'string') {
        reject(new Error('Failed to encode audio file'));
        return;
      }

      const commaIndex = result.indexOf(',');
      resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read audio file'));
    };

    reader.readAsDataURL(blob);
  });
};

/**
 * On localhost, rewrite GCS URLs to go through Vite dev proxy to avoid CORS.
 * Vite proxies /gcs-upload/* → https://storage.googleapis.com/*
 * Only applies when running locally — on staging/prod the URL goes directly to GCS.
 */
const proxyGcsUrl = (url: string): string => {
  const isLocalhost = window.location.hostname === 'localhost';

  if (isLocalhost && url.startsWith('https://storage.googleapis.com')) {
    return url.replace('https://storage.googleapis.com', '/gcs-upload');
  }

  return url;
};

/**
 * Upload file directly to a presigned URL via XMLHttpRequest.
 * Uses XHR instead of fetch to support upload progress tracking.
 */
export const uploadFileToPresignedUrl = (
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = proxyGcsUrl(uploadUrl);

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'audio/unknown');

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(
            `Failed to upload file to storage: ${xhr.status} ${xhr.statusText}`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error during file upload'));
    };

    xhr.send(file);
  });
};

/**
 * Get patient ID from current URL
 */
export const getPatientIdFromUrl = (): string => {
  const match = window.location.pathname.match(/patients\/([^/]+)/);
  return match?.[1] || '';
};
